import React, { useEffect, useRef, useState } from "react";
import {
  Form,
  PasswordInput,
  TextInput,
} from "@carbon/react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import CustomButton from '../components/CustomButton';
import { Link } from "react-router-dom"; 

const ROLE_CONFIG = {
  patient: {
    title: "患者ログイン",
    idLabel: "研究ID",
    next: "/patient-start",
  },
  attending_physician: {
    title: "主治医ログイン",
    idLabel: "主治医ID",
    next: "/patient-selection",
  },
  expert_physician: {
    title: "エキスパート医師ログイン",
    idLabel: "エキスパート医師ID",
    next: "/ap-selection",
  },
};

export default function Login() {
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname;

  const [role, setRole] = useState(null); // "patient" | "attending_physician" | "expert_physician" | null
  const [id, setId] = useState("");
  const [pw, setPw] = useState("");
  const [err, setErr] = useState("");
  const idInputRef = useRef(null);

  const cfg = role ? ROLE_CONFIG[role] : null;

  // PasswordInput を囲うラッパーに ref を当てて、内部の目ボタンを検索する
  const pwWrapRef = useRef(null);
  const didToggleVisibleRef = useRef(false); // ← 二重実行対策

  const toHalfWidth = (str) => {
    if (!str) return "";
    return str
      .replace(/[！-～]/g, (ch) =>
        String.fromCharCode(ch.charCodeAt(0) - 0xfee0)
      )
      .replace(/　/g, " "); // 全角スペース→半角
  };

  // ロールを選んだらID欄にフォーカス
  useEffect(() => {
    if (role) {
      setTimeout(() => idInputRef.current?.focus(), 0);
    }
  }, [role]);

  // 初回だけ“目ボタン”をクリックして「見える」状態にする
  useEffect(() => {
    if (!role || !pwWrapRef.current || didToggleVisibleRef.current) return;

    const toggleOnce = () => {
      if (!pwWrapRef.current || didToggleVisibleRef.current) return;
      const input = pwWrapRef.current.querySelector('input');
      if (!input) return;

      if (input.type === 'password') {
        const btn =
          pwWrapRef.current.querySelector('button.cds--text-input--password__visibility__toggle') ||
          pwWrapRef.current.querySelector('button[aria-label="パスワードを表示"]') ||
          pwWrapRef.current.querySelector('button[aria-label="Show password"]');

        if (btn) {
          btn.click();
          didToggleVisibleRef.current = true;
        }
      } else {
        didToggleVisibleRef.current = true;
      }
    };

    const raf = requestAnimationFrame(toggleOnce);
    return () => cancelAnimationFrame(raf);
  }, [role]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!role) return;
    setErr("");
    const res = await signIn({ id: id.trim(), role, password: pw }); // ← password を渡す
    if (res.ok) {
      navigate(from || cfg.next, { replace: true });
    } else {
      setErr(res.error || "ログインに失敗しました");
    }
  };

  const boxStyle = {
    background: "#fff",
    padding: "2rem",
    borderRadius: 4,
    margin: "1rem",
    maxWidth: 400,
    width: "100%",
    boxShadow: "0 0 10px rgba(0,0,0,0.1)",
  };

  return (
    <div style={{
      display: "flex",
      flexDirection: "column",
      gap: "1rem",
      alignItems: "center",
      justifyContent: "center",
      height: "100vh",
      background: "#f4f4f4",
      textAlign: "center",
      padding: "1rem",
    }}>
      <h1 style={{ marginBottom: role ? "0" : "1rem" }}>MS診察支援AIツール</h1>

      {!role && (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
          <CustomButton kind="primary" fullWidth style={{ marginBottom: "1rem" }} onClick={() => setRole("patient")}>
            患者
          </CustomButton>
          <CustomButton kind="primary" fullWidth style={{ marginBottom: "1rem" }} onClick={() => setRole("attending_physician")}>
            主治医
          </CustomButton>
          <CustomButton kind="primary" fullWidth style={{ marginBottom: 0 }} onClick={() => setRole("expert_physician")}>
            エキスパート医師
          </CustomButton>
        </div>
      )}

      {role && (
        <div style={boxStyle} aria-live="polite">
          <div style={{ marginBottom: "1rem", textAlign: "left" }}>
            <Link
              to="/"
              style={{
                textDecoration: "none",
                color: "#0062ff",
                fontWeight: "bold",
                fontSize: "1rem",
              }}
              onClick={() => {
                setRole(null);
                setId("");
                setPw("");
                setErr("");
              }}
            >
              ← 戻る
            </Link>
            <h2 style={{ marginTop: "1rem" }}>{cfg.title}</h2>
          </div>

          {err && <p style={{ color: "#da1e28", margin: "0 0 0.5rem 0", textAlign: "left" }}>{err}</p>}

          <Form onSubmit={handleSubmit}>
            <TextInput
              id="user-id"
              labelText={cfg.idLabel}
              placeholder="123456"
              style={{ marginBottom: "1rem" }}
              value={id}
              onChange={(e) => setId(toHalfWidth(e.target.value))}
              ref={idInputRef}
            />

            <div ref={pwWrapRef} style={{ marginTop: "1.5rem" }}>
              <PasswordInput
                id="password"
                labelText="パスワード"
                placeholder="********"
                value={pw}
                onChange={(e) => setPw(toHalfWidth(e.target.value))}
                showPasswordLabel="パスワードを表示"
                hidePasswordLabel="パスワードを非表示"
                autoComplete="current-password"
              />
            </div>

            <CustomButton kind="primary" type="submit" style={{ marginTop: "2rem" }} disabled={!id.trim() || !pw.trim()}>
              ログイン
            </CustomButton>
          </Form>
        </div>
      )}
    </div>
  );
}
